import { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { IDuplicationModel } from "./IDuplicationModel";
import type { IProjectModel } from "./IProjectModel";

/**
 * `DuplicationModel` — see {@link IDuplicationModel}.
 *
 * Unity reference: `Models/ProjectModel/DuplicationModel.cs`.
 *
 * Deviation from Unity: the buffer holds `(sceneId, entityId)` pairs rather
 * than live object handles, so a copied entity that is deleted before the
 * paste is skipped instead of being filtered out of `CanPaste` up front — the
 * web has no destroyed-handle sentinel to test. `canPaste` therefore re-checks
 * the ids against the model on every read, which is also what keeps the menu
 * item correct after an undo.
 */
export class DuplicationModel implements IDuplicationModel {
  readonly onChanged = new Emitter<void>();

  private readonly m_project: IProjectModel;
  /** Unity `m_buffer` + `m_bufferScene`. */
  private m_buffer: ReadonlyArray<{ sceneId: string; entityId: string }> = [];
  /** Unity `m_copiedPosition`. */
  private m_copiedPosition: Vec3 | null = null;

  constructor(project: IProjectModel) {
    this.m_project = project;
    // Unity drops the buffer with the model. The web keeps the ids across scene
    // switches (paste targets the ACTIVE scene, so the buffer outliving its
    // source scene is the point) but not across projects: an entity id from
    // another project can never resolve.
    project.onActiveProjectChanged.on(() => this.clear());
  }

  get canPaste(): boolean {
    return this.liveBuffer().length > 0;
  }

  get canPastePosition(): boolean {
    return this.m_copiedPosition !== null;
  }

  canCopyOrDuplicate(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean {
    if (snapshotIds.length === 0) return false;
    return snapshotIds.every((snapshotId) => {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      return snapshot?.isRoot === true;
    });
  }

  copyEntities(sceneId: string, snapshotIds: ReadonlyArray<string>): void {
    if (!this.canCopyOrDuplicate(sceneId, snapshotIds)) return;
    const buffer: Array<{ sceneId: string; entityId: string }> = [];
    for (const entityId of this.toEntityIds(sceneId, snapshotIds)) {
      buffer.push({ sceneId, entityId });
    }
    this.releaseBufferScene();
    this.m_buffer = buffer;
    // Unity `CopyGameObjects` pins the buffer's scene (`SetScenePersistent`)
    // because the paste reads the SOURCE rows: without the pin, switching scenes
    // and pasting would resolve nothing. `RemoveBuffer` un-pins it again.
    if (buffer.length > 0) this.m_project.setScenePersistent(sceneId, true);
    this.onChanged.emit();
  }

  async pasteEntitiesAsync(signal?: AbortSignal): Promise<ReadonlyArray<string>> {
    const targetSceneId = this.m_project.activeScene?.id;
    if (!targetSceneId) return [];
    const created: string[] = [];
    for (const entry of this.liveBuffer()) {
      const result = await this.m_project.duplicateEntityAsync(
        entry.sceneId,
        entry.entityId,
        signal,
        { targetSceneId },
      );
      if (result) created.push(result.entityId);
    }
    return created;
  }

  async duplicateEntitiesAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>> {
    if (!this.canCopyOrDuplicate(sceneId, snapshotIds)) return [];
    const created: string[] = [];
    for (const entityId of this.toEntityIds(sceneId, snapshotIds)) {
      const result = await this.m_project.duplicateEntityAsync(sceneId, entityId, signal);
      if (result) created.push(result.entityId);
    }
    return created;
  }

  copyPosition(sceneId: string, snapshotId: string): void {
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot || snapshot.isLocked) return;
    this.m_copiedPosition = snapshot.transform.position;
    this.onChanged.emit();
  }

  async pastePositionAsync(
    sceneId: string,
    snapshotId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const position = this.m_copiedPosition;
    if (!position) return;
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot || snapshot.isLocked) return;
    await this.m_project.updateEntitySnapshotAsync(
      sceneId,
      snapshotId,
      { transform: { position } },
      signal,
    );
  }

  clear(): void {
    if (this.m_buffer.length === 0 && this.m_copiedPosition === null) return;
    this.releaseBufferScene();
    this.m_buffer = [];
    this.m_copiedPosition = null;
    this.onChanged.emit();
  }

  /** Unity `RemoveBuffer`'s un-pin leg. */
  private releaseBufferScene(): void {
    for (const sceneId of new Set(this.m_buffer.map((entry) => entry.sceneId))) {
      this.m_project.setScenePersistent(sceneId, false);
    }
  }

  /** Buffer entries whose entity still exists (Unity's `go != null` filter). */
  private liveBuffer(): ReadonlyArray<{ sceneId: string; entityId: string }> {
    return this.m_buffer.filter(
      (entry) => this.m_project.getEntityById(entry.sceneId, entry.entityId) !== null,
    );
  }

  /** Root snapshot ids → their owning entity ids, de-duplicated. */
  private toEntityIds(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
  ): ReadonlyArray<string> {
    const out = new Set<string>();
    for (const snapshotId of snapshotIds) {
      const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
      if (entity) out.add(entity.id);
    }
    return [...out];
  }
}
